import LoanProductPage from "./LoanProductPage";

const IMG_PERSONAL = "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&q=80";
const IMG_DEBT = "https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=1200&q=80";
const IMG_EMERGENCY = "https://images.unsplash.com/photo-1516321310766-61f593d5f0ef?w=1200&q=80";
const IMG_MEDICAL = "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=1200&q=80";
const IMG_HOME = "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1200&q=80";
const IMG_AUTO = "https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=1200&q=80";
const IMG_BUSINESS = "https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=80";

const BASE_ELIGIBILITY = [
  "U.S. citizen or permanent resident, 18 years or older",
  "Verifiable source of income",
  "Active checking account for direct deposit",
  "Valid email and mobile phone number",
];

const BASE_FAQ = (name) => [
  { q: `Will applying for a ${name} affect my credit?`, a: "The initial request is informational and typically uses a soft credit inquiry that does not affect your score. A hard inquiry is only performed by a lending partner if you proceed to a full application." },
  { q: "How long does approval take?", a: "Many applicants receive a decision from a partner within minutes. Funding typically follows within 1–3 business days after acceptance." },
  { q: "Is my information secure?", a: "Yes. Data is encrypted in transit and at rest, and shared only with licensed lending partners you consent to be matched with." },
  { q: "Are there prepayment penalties?", a: "Our partners generally do not charge prepayment penalties on personal loans, but this varies by lender. Any applicable fees will be disclosed before you accept an offer." },
];

export function PersonalLoansPage() {
  return (
    <LoanProductPage
      slug="personal"
      overline="Personal Loans"
      title="Fixed-rate financing"
      italic="on your terms."
      subtitle="Unsecured personal loans for consolidating debt, life events, or unexpected expenses. Fixed monthly payments, no collateral required."
      hero={{ image: IMG_PERSONAL }}
      minAmount="$1,000"
      maxAmount="$50,000"
      aprRange="8.99% – 29.99% APR"
      termRange="24 – 84 months"
      monthlyExample="$207 / mo on $10,000 at 9.99% APR, 60-mo term"
      eligibility={BASE_ELIGIBILITY.concat(["Minimum credit score of 600 recommended"])}
      benefits={["No collateral required", "Fixed rates and fixed payments", "Funds typically in 1–3 business days", "No prepayment penalty (partner-dependent)"]}
      faqs={BASE_FAQ("personal loan")}
    />
  );
}

export function DebtConsolidationPage() {
  return (
    <LoanProductPage
      slug="debt-consolidation"
      overline="Debt Consolidation"
      title="One payment,"
      italic="less stress."
      subtitle="Roll multiple high-interest balances into a single fixed-rate loan with a predictable monthly payment. Simplify your finances and often save on interest."
      hero={{ image: IMG_DEBT }}
      minAmount="$2,500"
      maxAmount="$50,000"
      aprRange="7.99% – 24.99% APR"
      termRange="24 – 84 months"
      monthlyExample="$402 / mo on $20,000 at 8.99% APR, 60-mo term"
      eligibility={BASE_ELIGIBILITY.concat(["Existing high-interest debt (credit cards, personal loans)"])}
      benefits={["Consolidate multiple balances", "Potentially lower APR than credit cards", "Fixed payoff date", "Simplified monthly payment"]}
      faqs={BASE_FAQ("debt consolidation loan")}
    />
  );
}

export function EmergencyLoansPage() {
  return (
    <LoanProductPage
      slug="emergency"
      overline="Emergency Loans"
      title="Fast funding when"
      italic="you need it most."
      subtitle="Life happens. Get connected with partners who specialize in fast-turn personal loans for urgent expenses — with clear terms and no surprises."
      hero={{ image: IMG_EMERGENCY }}
      minAmount="$1,000"
      maxAmount="$25,000"
      aprRange="9.99% – 35.99% APR"
      termRange="12 – 60 months"
      monthlyExample="$107 / mo on $5,000 at 12.99% APR, 60-mo term"
      eligibility={BASE_ELIGIBILITY}
      benefits={["Same-day pre-qualification", "Funds as fast as next business day", "No collateral", "Transparent fees"]}
      faqs={BASE_FAQ("emergency loan")}
    />
  );
}

export function MedicalLoansPage() {
  return (
    <LoanProductPage
      slug="medical"
      overline="Medical Loans"
      title="Health care coverage,"
      italic="on your schedule."
      subtitle="Financing for medical procedures, dental, fertility, cosmetic, and elective care — including expenses insurance doesn't cover."
      hero={{ image: IMG_MEDICAL }}
      minAmount="$1,000"
      maxAmount="$50,000"
      aprRange="7.99% – 27.99% APR"
      termRange="24 – 84 months"
      monthlyExample="$308 / mo on $15,000 at 9.99% APR, 60-mo term"
      eligibility={BASE_ELIGIBILITY}
      benefits={["Covers what insurance doesn't", "No collateral required", "Fixed monthly payments", "Use with your chosen provider"]}
      faqs={BASE_FAQ("medical loan")}
    />
  );
}

export function HomeImprovementPage() {
  return (
    <LoanProductPage
      slug="home-improvement"
      overline="Home Improvement"
      title="Finance the project,"
      italic="not just the dream."
      subtitle="Fund renovations, repairs, or additions without tapping home equity. Unsecured, fixed-rate financing for homeowners and renters."
      hero={{ image: IMG_HOME }}
      minAmount="$2,500"
      maxAmount="$100,000"
      aprRange="6.99% – 22.99% APR"
      termRange="36 – 120 months"
      monthlyExample="$488 / mo on $25,000 at 8.99% APR, 72-mo term"
      eligibility={BASE_ELIGIBILITY.concat(["Property does not need to be used as collateral"])}
      benefits={["No home appraisal required", "Fast funding — no HELOC delays", "Fixed rate for the entire term", "Use for any home-related project"]}
      faqs={BASE_FAQ("home improvement loan")}
    />
  );
}

export function AutoLoansPage() {
  return (
    <LoanProductPage
      slug="auto"
      overline="Auto Loans"
      title="New, used, or refinance —"
      italic="drive better rates."
      subtitle="Financing for new & used vehicles, refinance existing auto loans, and lease buyouts. Get pre-qualified before you visit the dealer."
      hero={{ image: IMG_AUTO }}
      minAmount="$5,000"
      maxAmount="$100,000"
      aprRange="5.49% – 18.99% APR"
      termRange="24 – 84 months"
      monthlyExample="$382 / mo on $20,000 at 6.99% APR, 60-mo term"
      eligibility={BASE_ELIGIBILITY.concat(["Vehicle meets partner age/mileage guidelines"])}
      benefits={["Pre-qualification before shopping", "Refinance to lower your rate", "Lease buyout financing", "Transparent, dealer-negotiation-ready terms"]}
      faqs={BASE_FAQ("auto loan")}
    />
  );
}

export function BusinessLoansPage() {
  return (
    <LoanProductPage
      slug="business"
      overline="Business Loans"
      title="Capital that keeps up"
      italic="with your growth."
      subtitle="Working capital, equipment financing, and expansion capital for U.S. small and mid-size businesses. Term loans and lines of credit."
      hero={{ image: IMG_BUSINESS }}
      minAmount="$10,000"
      maxAmount="$500,000"
      aprRange="7.99% – 29.99% APR"
      termRange="12 – 120 months"
      monthlyExample="$1,982 / mo on $100,000 at 8.99% APR, 60-mo term"
      eligibility={[
        "U.S. registered business, 6+ months in operation",
        "Minimum annual revenue of $50,000",
        "Business owner age 18+",
        "Business checking account",
      ]}
      benefits={["Term loans & lines of credit", "Equipment financing", "Working capital", "SBA-eligible partners available"]}
      faqs={BASE_FAQ("business loan")}
    />
  );
}
